import React, { useEffect, useRef, useState } from "react";

import {
  Avatar,
  Badge,
  Button,
  Typography,
  message,
} from "antd";

import {
  UserOutlined,
  LogoutOutlined,
  MessageOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  CameraOutlined,
} from "@ant-design/icons";

import api, { BASE_URL } from "../api/api";
import AddAcademyYear from "./AcademyYearSelection/AddAcademyYear";
import AdminPublishGradesPopup from "../SchoolDashApp/AdminPublishGradesPopup";
import AdminResetPassword from "../AdminResetPassword";

import { Link, useNavigate } from "react-router-dom";

import { motion } from "framer-motion";

const { Text } = Typography;

const PROFILE_UPDATED_EVENT = "profileUpdated";

const MAX_PROFILE_IMAGE_SIZE = 5 * 1024 * 1024;

const ProfileImageDashboard = ({
  theme,
  header = false,
  overview = false,
}) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);

  const [openAcademyYear, setOpenAcademyYear] = useState(false);
  const [openPublishGrades, setOpenPublishGrades] = useState(false);
  const [openResetPassword, setOpenResetPassword] = useState(false);

  const safeTheme = {
    primary: theme?.primary || "#C0750D",
    secondary: theme?.secondary || "#64748B",
    text: theme?.text || "#0F172A",
    border: theme?.border || "#E2E8F0",
    card: theme?.card || "#FFFFFF",
    soft: theme?.soft || "#F1F5F9",
    mode: theme?.mode || "light",
    darkMode: theme?.darkMode || false,
  };

  const fetchAcademicYears = async () => {
    try {
      await api.get("/academic-years/");

      window.dispatchEvent(
        new Event("academicYearUpdated")
      );
    } catch (error) {
      console.log(
        "Academic years fetch error:",
        error
      );
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get("/user-profile/");

      setUser(res.data);
    } catch (error) {
      console.log(
        "Profile fetch error:",
        error
      );
    }
  };

  useEffect(() => {
    fetchProfile();

    const updateProfile = () => {
      fetchProfile();
    };

    window.addEventListener(
      PROFILE_UPDATED_EVENT,
      updateProfile
    );

    return () => {
      window.removeEventListener(
        PROFILE_UPDATED_EVENT,
        updateProfile
      );
    };
  }, []);

  const getProfileImage = () => {
    if (!user?.profile_image) {
      return null;
    }

    if (
      user.profile_image.startsWith("http://") ||
      user.profile_image.startsWith("https://")
    ) {
      return `${user.profile_image}${
        user.profile_image.includes("?")
          ? "&"
          : "?"
      }v=${Date.now()}`;
    }

    return `${BASE_URL}${user.profile_image}${
      user.profile_image.includes("?")
        ? "&"
        : "?"
    }v=${Date.now()}`;
  };

  const openImageSelector = () => {
    if (uploading) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      message.error(
        "Please upload a JPG, PNG, or WebP image."
      );

      event.target.value = "";
      return;
    }

    if (file.size > MAX_PROFILE_IMAGE_SIZE) {
      message.error(
        "Profile image must not exceed 5 MB."
      );

      event.target.value = "";
      return;
    }

    const formData = new FormData();

    formData.append(
      "profile_image",
      file
    );

    try {
      setUploading(true);

      const res = await api.put(
        "/user-profile/",
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      setUser(res.data);

      window.dispatchEvent(
        new Event(PROFILE_UPDATED_EVENT)
      );

      message.success(
        "Profile image updated successfully."
      );
    } catch (error) {
      console.log(
        "Profile image update error:",
        error
      );

      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.profile_image?.[0] ||
          "Profile image update failed."
      );
    } finally {
      setUploading(false);

      event.target.value = "";
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/api/logout/");
    } catch (error) {
      console.log(
        "Logout error:",
        error
      );
    } finally {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");

      window.dispatchEvent(
        new Event("force-logout")
      );

      navigate("/login", {
        replace: true,
      });
    }
  };

  const displayName =
    user?.username || "Loading...";

  const email =
    user?.email || "";

  const userType =
    user?.user_type || "";

  const authorizedUsers = [
    "admin",
    "registry",
    "fiscal affairs",
    "principal",
    "vpi",
    "vpa",
    "dean",
    "it personel",
  ];

  const canManageSchool =
    authorizedUsers.includes(
      String(userType).toLowerCase()
    );

  const avatarSize = overview
    ? 50
    : header
    ? 46
    : 72;

  const cameraSize = overview
    ? 20
    : header
    ? 19
    : 25;

  const cameraIconSize = overview
    ? 10
    : header
    ? 9
    : 12;

  const cameraBorder = overview
    ? 2
    : header
    ? 2
    : 2;

  const profileAvatar = (
    <div
      className="profile-avatar-wrapper"
      onClick={openImageSelector}
      title={
        uploading
          ? "Updating profile image..."
          : "Change profile image"
      }
      style={{
        position: "relative",
        width: avatarSize,
        height: avatarSize,
        flex: `0 0 ${avatarSize}px`,
        cursor: uploading
          ? "wait"
          : "pointer",
      }}
    >
      <Badge
        dot
        status={
          user?.is_online
            ? "success"
            : "default"
        }
        offset={
          overview
            ? [-2, 40]
            : header
            ? [-2, 36]
            : [-4, 59]
        }
      >
        <Avatar
          size={avatarSize}
          src={getProfileImage()}
          icon={<UserOutlined />}
          style={{
            width: avatarSize,
            height: avatarSize,
            border: `2px solid ${safeTheme.primary}`,
            background: safeTheme.soft,
            opacity: uploading ? 0.55 : 1,
            boxShadow:
              safeTheme.mode === "dark"
                ? "0 3px 12px rgba(0,0,0,.25)"
                : "0 3px 12px rgba(6,42,75,.10)",
            transition:
              "opacity .2s ease, transform .2s ease",
          }}
        />
      </Badge>

      <div
        className="profile-camera-button"
        onClick={(event) => {
          event.stopPropagation();
          openImageSelector();
        }}
        style={{
          position: "absolute",
          right: overview
            ? -2
            : header
            ? -2
            : -3,
          bottom: overview
            ? -1
            : header
            ? -1
            : -2,
          width: cameraSize,
          height: cameraSize,
          borderRadius: "50%",
          background:
            safeTheme.primary,
          color: "#fff",
          border: `${cameraBorder}px solid ${
            safeTheme.mode === "dark"
              ? "#1e293b"
              : "#ffffff"
          }`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: uploading
            ? "wait"
            : "pointer",
          boxShadow:
            "0 2px 6px rgba(0,0,0,.18)",
          zIndex: 5,
        }}
      >
        {uploading ? (
          <span
            style={{
              width:
                cameraIconSize + 2,
              height:
                cameraIconSize + 2,
              border:
                "2px solid rgba(255,255,255,.45)",
              borderTopColor:
                "#ffffff",
              borderRadius: "50%",
              display: "block",
              animation:
                "profileImageSpin .8s linear infinite",
            }}
          />
        ) : (
          <CameraOutlined
            style={{
              fontSize: cameraIconSize,
              lineHeight: 1,
            }}
          />
        )}
      </div>
    </div>
  );

  if (overview) {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={handleImageChange}
          style={{
            display: "none",
          }}
        />

        <div
          className="overview-profile"
          style={{
            minWidth: 0,
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {profileAvatar}

          <div
            className="overview-profile-details"
            style={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 3,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "3px 7px",
                minWidth: 0,
              }}
            >
              <Text
                strong
                ellipsis
                style={{
                  color: safeTheme.text,
                  fontSize: 15,
                  lineHeight: 1.15,
                  fontWeight: 800,
                  maxWidth: 190,
                }}
              >
                {displayName}
              </Text>

              {userType && (
                <>
                  <span
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background:
                        safeTheme.secondary,
                      opacity: 0.55,
                      flexShrink: 0,
                    }}
                  />

                  <Text
                    ellipsis
                    style={{
                      color:
                        safeTheme.primary,
                      fontSize: 12,
                      lineHeight: 1.15,
                      fontWeight: 700,
                      textTransform:
                        "capitalize",
                      maxWidth: 130,
                    }}
                  >
                    {userType}
                  </Text>
                </>
              )}
            </div>

            {email && (
              <Text
                ellipsis
                style={{
                  display: "block",
                  color:
                    safeTheme.secondary,
                  fontSize: 12,
                  lineHeight: 1.15,
                  maxWidth: 300,
                }}
              >
                {email}
              </Text>
            )}

            <Text
              className="profile-image-hint"
              style={{
                display: "block",
                color:
                  safeTheme.secondary,
                fontSize: 9,
                lineHeight: 1.1,
                opacity: 0.8,
              }}
            >
              JPG, PNG or WebP • Max 5 MB
            </Text>
          </div>

         
        </div>

        <style>
          {`
            @keyframes profileImageSpin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }

            .profile-avatar-wrapper:hover .profile-camera-button {
              transform: scale(1.08);
            }

            .profile-camera-button {
              transition: transform .18s ease;
            }

            .overview-profile {
              max-width: 100%;
            }

            .overview-profile-details {
              max-width: 320px;
            }

            @media (max-width: 1200px) {
              .overview-profile {
                gap: 9px !important;
              }

              .overview-profile-details {
                max-width: 240px !important;
              }

              .overview-greeting {
                margin-left: 0 !important;
              }

              .overview-greeting > span {
                font-size: 15px !important;
              }

              .overview-greeting > span:last-child {
                font-size: 18px !important;
              }

              .profile-image-hint {
                display: none !important;
              }
            }

            @media (max-width: 900px) {
              .overview-profile-details {
                max-width: 280px !important;
              }
            }

            @media (max-width: 600px) {
              .overview-profile {
                gap: 8px !important;
                align-items: center !important;
              }

              .overview-profile-details {
                max-width: calc(100vw - 150px) !important;
              }

              .overview-greeting {
                width: 100%;
                margin-top: 1px;
              }

              .overview-greeting > span {
                font-size: 15px !important;
              }

              .overview-greeting > span:last-child {
                font-size: 18px !important;
              }

              .profile-image-hint {
                display: none !important;
              }
            }

            @media (max-width: 460px) {
              .overview-profile {
                align-items: flex-start !important;
              }

              .overview-profile-details {
                max-width: calc(100vw - 90px) !important;
              }

              .overview-profile-details > div:first-child {
                flex-wrap: nowrap !important;
              }

              .overview-profile-details > div:first-child > span:first-child {
                max-width: 135px !important;
              }

              .overview-profile-details > div:first-child > span:nth-last-child(1) {
                max-width: 95px !important;
              }

              .overview-greeting {
                position: absolute;
                left: 0;
                top: 54px;
              }
            }
          `}
        </style>
      </>
    );
  }

  if (header) {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={handleImageChange}
          style={{
            display: "none",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            minWidth: 0,
            width: "100%",
          }}
        >
          {profileAvatar}

          <div
            style={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Text
              strong
              ellipsis
              style={{
                color: safeTheme.text,
                fontSize: 15,
                lineHeight: 1.15,
                fontWeight: 800,
              }}
            >
              {displayName}
            </Text>

            <Text
              ellipsis
              style={{
                color:
                  safeTheme.secondary,
                fontSize: 12,
                lineHeight: 1.15,
              }}
            >
              {email}
            </Text>

            <Text
              style={{
                color:
                  safeTheme.secondary,
                fontSize: 9,
                lineHeight: 1.1,
                opacity: 0.8,
              }}
            >
              JPG / PNG / WebP • 5 MB max
            </Text>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        onChange={handleImageChange}
        style={{
          display: "none",
        }}
      />

      <div
        className="profile-sidebar"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "17px 12px",
          margin: "14px 0",
          borderRadius: 16,
          background:
            safeTheme.mode === "dark"
              ? "#1e293b"
              : "#ffffff",
          border:
            `1px solid ${safeTheme.border}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
        }}
      >
        {profileAvatar}

        <div
          style={{
            width: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 4,
          }}
        >
          <Text
            strong
            ellipsis
            style={{
              width: "100%",
              color: safeTheme.text,
              fontSize: 16,
              lineHeight: 1.2,
              fontWeight: 800,
            }}
          >
            {displayName}
          </Text>

          {email && (
            <Text
              ellipsis
              style={{
                width: "100%",
                color:
                  safeTheme.secondary,
                fontSize: 12,
                lineHeight: 1.2,
              }}
            >
              {email}
            </Text>
          )}

          {userType && (
            <Text
              style={{
                color: safeTheme.primary,
                fontSize: 12,
                lineHeight: 1.2,
                fontWeight: 700,
                textTransform:
                  "capitalize",
              }}
            >
              {userType}
            </Text>
          )}

          <Text
            style={{
              marginTop: 2,
              color:
                safeTheme.secondary,
              fontSize: 10,
              lineHeight: 1.2,
              opacity: 0.85,
            }}
          >
            JPG, PNG or WebP • Max 5 MB
          </Text>

          <button
            type="button"
            onClick={() =>
              setOpenResetPassword(true)
            }
            style={{
              border: "none",
              background: "transparent",
              padding: "3px 0",
              marginTop: 3,
              color: "#ff6b00",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            User Authentication Login
          </button>
        </div>

        {canManageSchool && (
          <div
            style={{
              width: "100%",
              marginTop: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 9,
            }}
          >
            <Link
              to="/chatApp"
              style={{
                width: "100%",
                textDecoration: "none",
              }}
            >
              <Button
                block
                icon={<MessageOutlined />}
                style={{
                  height: 40,
                  borderRadius: 9,
                  fontWeight: 700,
                }}
              >
                Chats
              </Button>
            </Link>

            <Button
              block
              icon={<FileDoneOutlined />}
              onClick={() =>
                setOpenPublishGrades(true)
              }
              style={{
                height: 40,
                borderRadius: 9,
                fontWeight: 700,
                color: "#fff",
                background: "#800000",
                borderColor: "#800000",
              }}
            >
              Manage Grade Publication
            </Button>

            <Button
              block
              icon={<CalendarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setOpenAcademyYear(true);
              }}
              style={{
                height: 40,
                borderRadius: 9,
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              Add Academic Year
            </Button>

            <Button
              block
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                height: 40,
                borderRadius: 9,
                fontWeight: 700,
              }}
            >
              Logout
            </Button>
          </div>
        )}
      </div>

      <AddAcademyYear
        open={openAcademyYear}
        handleClose={() => {
          setOpenAcademyYear(false);
        }}
        refreshYears={fetchAcademicYears}
      />

      <AdminPublishGradesPopup
        open={openPublishGrades}
        onClose={() =>
          setOpenPublishGrades(false)
        }
      />

      <AdminResetPassword
        open={openResetPassword}
        onClose={() =>
          setOpenResetPassword(false)
        }
      />

      <style>
        {`
          @keyframes profileImageSpin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          .profile-avatar-wrapper:hover .profile-camera-button {
            transform: scale(1.08);
          }

          .profile-camera-button {
            transition: transform .18s ease;
          }

          .profile-sidebar {
            max-width: 100%;
          }

          @media (max-width: 600px) {
            .profile-sidebar {
              padding: 14px 10px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default ProfileImageDashboard;

